// Story scroll (blocklist/Components/storyScroll.cshtml), naar het voorbeeld van de story-scroll op retool.com.
// Het blok staat vast (sticky) zodra het het scherm vult; doorscrollen gaat dan binnen het blok: elke 60vh wordt het
// volgende item actief (tekst klapt open, afbeeldingen schuiven mee). Na het laatste item laat het blok los.
// Klik op een titel of stip scrollt naar dat item. Zonder JavaScript staan alle items open (geen .is-enhanced).
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const STEP = 0.6; // scrollafstand per item als deel van de schermhoogte (gelijk aan $story-step in blocks/_story-scroll.scss)
const GAP = 28; // ruimte tussen de afbeeldingen in px ($story-image-gap)
const INACTIVE_SCALE = 0.9; // niet-actieve afbeeldingen zijn 90% breed (en dus 90% hoog) op desktop
const desktop = window.matchMedia('(min-width: 1024px)');

class StoryScroll {
    private items: HTMLElement[];
    private images: HTMLElement[];
    private dots: HTMLElement[];
    private track: HTMLElement;
    private current = -1;
    private ticking = false;

    constructor(private section: HTMLElement) {
        this.items = Array.from(section.querySelectorAll<HTMLElement>('.c-story__item'));
        this.images = Array.from(section.querySelectorAll<HTMLElement>('.c-story__image'));
        this.dots = Array.from(section.querySelectorAll<HTMLElement>('.c-story__dot'));
        // De scrollbaan is de ouder van het vastgezette paneel
        const inner = section.querySelector<HTMLElement>('.c-story__inner');
        this.track = inner && inner.parentElement ? inner.parentElement : section;

        section.classList.add('is-enhanced');

        this.items.forEach((item, i) => {
            const trigger = item.querySelector<HTMLElement>('.c-story__trigger');
            if (trigger) { trigger.addEventListener('click', () => this.goTo(i)); }
        });
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

        const request = () => {
            if (this.ticking) { return; }
            this.ticking = true;
            window.requestAnimationFrame(() => {
                this.ticking = false;
                this.update();
            });
        };
        // capture op document: vangt ook scroll-events van een andere scroll-container dan het venster
        document.addEventListener('scroll', request, { passive: true, capture: true });
        window.addEventListener('resize', request);
        this.update();
    }

    /** Hoe ver de scrollbaan al voorbij de bovenkant van het scherm is, in stappen van 60vh. */
    private update() {
        const passed = -this.track.getBoundingClientRect().top;
        const index = Math.floor(passed / (window.innerHeight * STEP));
        this.activate(Math.max(0, Math.min(this.items.length - 1, index)));
        this.position();
    }

    /**
     * Desktop: verticale positie per afbeelding t.o.v. het kader. De actieve staat op 0; de andere zijn kleiner
     * (INACTIVE_SCALE) en staan erboven (vorige) of eronder (volgende), steeds GAP px ertussen.
     */
    private position() {
        const media = this.section.querySelector<HTMLElement>('.c-story__media');
        if (!desktop.matches || !media) { return; }
        const active = media.getBoundingClientRect().height;
        const small = active * INACTIVE_SCALE;
        this.images.forEach((image, i) => {
            const d = i - this.current;
            const y = d === 0 ? 0 : d > 0 ? active + GAP + (d - 1) * (small + GAP) : d * (small + GAP);
            image.style.setProperty('--story-y', Math.round(y) + 'px');
        });
    }

    private activate(index: number) {
        if (index === this.current) { return; }
        this.current = index;
        this.section.style.setProperty('--story-index', String(index));

        const toggle = (list: HTMLElement[]) => list.forEach((el, i) => el.classList.toggle('is-active', i === index));
        toggle(this.items);
        toggle(this.images);
        toggle(this.dots);
        this.items.forEach((item, i) => {
            const trigger = item.querySelector('.c-story__trigger');
            if (trigger) { trigger.setAttribute('aria-expanded', String(i === index)); }
        });
        this.dots.forEach((dot, i) => {
            if (i === index) { dot.setAttribute('aria-current', 'true'); } else { dot.removeAttribute('aria-current'); }
        });
    }

    /** Scrollt naar het midden van de stap van item i (het blok staat dan vast met dat item actief). */
    private goTo(index: number) {
        const step = window.innerHeight * STEP;
        const trackTop = window.pageYOffset + this.track.getBoundingClientRect().top;
        window.scrollTo({ top: trackTop + (index + 0.5) * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
}

for (const section of Array.from(document.querySelectorAll<HTMLElement>('.js-story'))) {
    new StoryScroll(section);
}

export {};
